import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { COUNTRIES } from "@/lib/countries";
import { useLang } from "@/lib/i18n";

const panelCls =
  "absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-lg border border-[#262930] bg-[#121418] shadow-2xl shadow-black/50";
const searchCls =
  "w-full border-b border-[#262930] bg-[#0E1013] px-4 py-3 text-base md:text-sm text-[#EAE6E1] placeholder:text-[#686D76] outline-none";
const btnCls =
  "flex w-full items-center justify-between rounded-lg border border-[#262930] bg-[#121418] px-4 py-3 md:py-3.5 text-sm text-[#EAE6E1] transition-colors duration-200 hover:border-[#3A3F4B] focus:border-[#8C9A86] outline-none";

function useOutsideClose(ref, close) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, close]);
}

export function CountryPicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const { t } = useLang();
  useOutsideClose(ref, () => setOpen(false));

  const selected = COUNTRIES.find((c) => c.name === value);
  const filtered = COUNTRIES.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid={testId} onClick={() => setOpen((v) => !v)} className={btnCls}>
        <span className="flex items-center gap-2.5 truncate">
          {selected ? (
            <>
              <span className="text-base leading-none">{selected.flag}</span>
              <span className="truncate">{value}</span>
            </>
          ) : (
            <span className="text-[#686D76]">{t("contact.countryPlaceholder")}</span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#686D76] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={panelCls} data-testid={`${testId}-panel`}>
          <input
            autoFocus
            data-testid={`${testId}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("contact.countrySearch")}
            className={searchCls}
          />
          <div className="max-h-60 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.name}
                type="button"
                data-testid={`${testId}-option-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setQ("");
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#9E9E9E] transition-colors duration-150 hover:bg-[#181A20] hover:text-[#EAE6E1]"
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="font-tech text-xs text-[#686D76]">{c.dial}</span>
                {c.name === value && <Check className="h-3.5 w-3.5 text-[#8C9A86]" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-[#686D76]">{t("contact.noCountry")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function DialCodePicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const { t } = useLang();
  useOutsideClose(ref, () => setOpen(false));

  const withDial = COUNTRIES.filter((c) => c.dial);
  const selected = withDial.find((c) => c.dial === value);
  const filtered = withDial.filter(
    (c) => c.dial.replace(/\s/g, "").includes(q.replace(/\s/g, "")) || c.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid={testId} onClick={() => setOpen((v) => !v)} className={btnCls}>
        <span className="flex items-center gap-2 truncate">
          {selected ? (
            <>
              <span className="text-base leading-none">{selected.flag}</span>
              <span className="font-tech">{value}</span>
            </>
          ) : (
            <span className="text-[#686D76]">{t("contact.dialCode")}</span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#686D76] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`${panelCls} min-w-64`} data-testid={`${testId}-panel`}>
          <input
            autoFocus
            data-testid={`${testId}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("contact.dialSearch")}
            className={searchCls}
          />
          <div className="max-h-60 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.name}
                type="button"
                data-testid={`${testId}-option-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                onClick={() => {
                  onChange(c.dial);
                  setOpen(false);
                  setQ("");
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#9E9E9E] transition-colors duration-150 hover:bg-[#181A20] hover:text-[#EAE6E1]"
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="font-tech text-xs text-[#EAE6E1] w-14 shrink-0">{c.dial}</span>
                <span className="flex-1 truncate">{c.name}</span>
                {c.dial === value && <Check className="h-3.5 w-3.5 text-[#8C9A86]" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-[#686D76]">{t("contact.noCode")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
